/**
 * activityAdapter — une activité de l'API (`ActivityResource`) → `UIActivity`.
 *
 * Port littéral de ce que lit la ligne du legacy :
 * - titre : `material.title` avec le titre de l'activité en repli (`RealizationsListItemContent.js:216-219`)
 * - sous-titre : le chapitre travaillé, sinon le nom de la matière (`…:240-244`)
 * - chronomètre : `duration` est en **secondes** et s'affiche en minutes entières,
 *   `estimated_duration` est déjà en minutes (`TodoActivityBadges.js:35-66`)
 * - date + statut : `finished_at` et `status` (`…:255-275`, `DoneChip.js:9-50`)
 *
 * La matière est rapprochée par `school_subject_id` dans le catalogue `staticData.school_subjects`,
 * comme `findItemInListWithId(activity?.school_subject_id, props.schoolSubjects)`
 * (`RealizationsListItem.js:50-53`).
 */
import { type UIActivity, type UIActivityStatus } from "../../../ui/types"
import { indexSubjectsById, toUISubject, type ApiSchoolSubject } from "../shared/schoolSubjectAdapter"

/** Forme relâchée de `ActivityResource` — seuls les champs que la ligne affiche. */
export type ApiActivity = {
    id?: string | number
    title?: string | null
    status?: string | null
    /** Minutes, prévues par le support. */
    estimated_duration?: number | null
    /** Secondes réellement passées — `null` tant que l'activité n'a pas été ouverte. */
    duration?: number | null
    /** `Y-m-d H:i` (local, `Tz::convertToLocal`). */
    finished_at?: string | null
    school_subject_id?: string | number | null
    material?: { title?: string | null } | null
    student_chapter?: { chapter?: { name?: string | null } | null } | null
}

/**
 * Les sept statuts que `DoneChip` sait nommer. Tout le reste retombe sur `validated`, qui est sa
 * branche par défaut (`DoneChip.js:36-50`) — on ne jette pas une ligne pour un statut inconnu.
 */
const KNOWN_STATUSES: UIActivityStatus[] = [
    "todo",
    "started",
    "ongoing",
    "not_reviewed",
    "pending_review",
    "rejected",
    "validated",
]

export function toUIActivityStatus(status: string | null | undefined): UIActivityStatus {
    const known = KNOWN_STATUSES.find((s) => s === status)
    return known ?? "validated"
}

/**
 * `duration` (secondes) → minutes entières, comme `Math.floor(activity?.duration / 60)`.
 * Absente, elle le reste : le chronomètre écrit alors un tiret, et « zéro minute » ne veut pas
 * dire « pas encore commencée ».
 */
function spentMinutes(duration: number | null | undefined): number | null {
    return duration == null ? null : Math.floor(duration / 60)
}

export function toUIActivity(
    api: ApiActivity | null | undefined,
    subjectsById: Record<string, ApiSchoolSubject>
): UIActivity | null {
    if (api?.id == null) return null

    const subject = subjectsById[String(api.school_subject_id ?? "")]
    const chapterName = api.student_chapter?.chapter?.name ?? undefined

    return {
        id: api.id,
        title: api.material?.title ?? api.title ?? "",
        subject: toUISubject(subject),
        chapterName: chapterName || undefined,
        status: toUIActivityStatus(api.status),
        estimatedMinutes: api.estimated_duration ?? 0,
        spentMinutes: spentMinutes(api.duration),
        finishedAt: api.finished_at ?? undefined,
    }
}

export function toUIActivities(
    activities: ApiActivity[] | null | undefined,
    subjects: ApiSchoolSubject[] | null | undefined
): UIActivity[] {
    const subjectsById = indexSubjectsById(subjects)
    return (activities ?? [])
        .map((activity) => toUIActivity(activity, subjectsById))
        .filter((activity): activity is UIActivity => activity != null)
}
